import { Close } from '@mui/icons-material';
import { Box, Drawer, IconButton, Stack, SxProps, Tab, Tabs, Theme, Typography } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { DisplayMoney } from '@price-for-profit/data-grid';
import { PropsWithChildren, useState } from 'react';
import { FormattedNumber } from 'shared/components';
import { useCustomerDeliveredPrices } from 'shared/queries';
import { CommoditySqlView } from 'shared/types';
import { v4 as uuid } from 'uuid';

const drawerWidth = 375;

const drawerStyles: SxProps<Theme> = {
  width: drawerWidth,
  flexShrink: 0,
};

interface ProductDetailDrawerProps {
  productDetail: CommoditySqlView;
  closeDrawer(): void;
}

interface ProductMetric {
  title: string;
  component: JSX.Element;
}

export function ProductDetailDrawer({ productDetail, closeDrawer }: ProductDetailDrawerProps) {
  const [value, setValue] = useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Drawer
      sx={drawerStyles}
      variant='persistent'
      anchor='right'
      open={productDetail.id !== undefined}
      PaperProps={{
        sx: {
          width: drawerWidth + 24,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 1,
        }}
      >
        <Typography>Additional Information: {productDetail.productNumber}</Typography>
        <IconButton onClick={closeDrawer}>
          <Close />
        </IconButton>
      </Box>
      <Stack>
        <Tabs variant='fullWidth' value={value} onChange={handleChange}>
          <Tab label='Customer Prices' />
          <Tab label='Product Details' />
        </Tabs>
        <TabPanel value={value} index={0}>
          <DisplayCustomerPrices productNumber={productDetail.productNumber} />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <ProductMetrics productDetail={productDetail} />
        </TabPanel>
      </Stack>
    </Drawer>
  );
}

interface TabPanelProps {
  index: number;
  value: number;
}

function TabPanel(props: PropsWithChildren<TabPanelProps>) {
  const { children, value, index } = props;

  return (
    <div role='tabpanel' hidden={value !== index}>
      {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
    </div>
  );
}
interface DisplayCustomerPricesProps {
  productNumber: string;
}
const customerPriceColumns: GridColDef[] = [
  {
    headerName: 'Customer',
    field: 'billToCustCode',
    flex: 1,
  },
  {
    headerName: 'Segment',
    field: 'revisedSegment',
    flex: 1,
  },
  {
    headerName: 'Price Level',
    field: 'revisedPriceLevel',
    flex: 1,
  },
  {
    headerName: 'Price',
    field: 'finalPrice',
    flex: 1,
    align: 'right',
    renderCell: DisplayMoney(),
  },
];
function DisplayCustomerPrices({ productNumber }: DisplayCustomerPricesProps) {
  const { customerPrices, isLoading } = useCustomerDeliveredPrices(productNumber);

  return (
    <Box height={'calc(100vh - 165px)'}>
      <DataGridPro
        density='compact'
        getRowId={r => uuid()}
        columns={customerPriceColumns}
        rows={customerPrices || []}
        loading={isLoading}
        disableColumnMenu
        hideFooter
      />
    </Box>
  );
}

interface ProductMetricsProps {
  productDetail: CommoditySqlView;
}

function ProductMetrics({ productDetail }: ProductMetricsProps) {
  const metrics: ProductMetric[] = [
    {
      title: 'Product Number',
      component: <Typography>{productDetail.productNumber}</Typography>,
    },
    {
      title: 'TTM Revenue',
      component: <FormattedNumber value={productDetail.ttmRevenue} prefix={'$'} decimalScale={2} />,
    },
    {
      title: 'TTM Cases Sold',
      component: <FormattedNumber value={productDetail.ttmCasesSold} />,
    },
    {
      title: 'Two Week Volume',
      component: <FormattedNumber value={productDetail.twoWeekVolume} />,
    },
    {
      title: 'Prior Two Week Volume',
      component: <FormattedNumber value={productDetail.priorTwoWeekVolume} />,
    },
    {
      title: 'Volume Change',
      component: <FormattedNumber value={Number(productDetail.volumeChange) * 100} decimalScale={1} suffix={'%'} />,
    },
    {
      title: 'Two Week Customer Count',
      component: <FormattedNumber value={productDetail.twoWeekCustomerCount} />,
    },
    {
      title: 'Model Cost Movement Classification',
      component: <Typography>{productDetail.modelCostMovementClassification}</Typography>,
    },
    {
      title: 'Seasonality Margin Factor',
      component: <FormattedNumber value={Number(productDetail.seasonalityMarginFactor) * 100} decimalScale={1} />,
    },
    {
      title: 'Holiday Margin Factor',
      component: <FormattedNumber value={Number(productDetail.holidayMarginFactor) * 100} decimalScale={1} />,
    },
    {
      title: 'Seller Adherence',
      component: <FormattedNumber value={Number(productDetail.sellerAdherence) * 100} decimalScale={1} suffix={'%'} />,
    },
    {
      title: 'Buyer Adherence',
      component: <FormattedNumber value={Number(productDetail.buyerAdherence) * 100} decimalScale={1} suffix={'%'} />,
    },
  ];
  return (
    <>
      {metrics.map(m => (
        <Stack key={m.title}>
          <Typography variant='caption'>{m.title}</Typography>
          {m.component}
        </Stack>
      ))}
    </>
  );
}
